import React, { useState, useCallback } from 'react';
import { OKRState, OneOnOneMeeting, OneOnOneActionItem, Task } from '../types';
import { computeSmartScore, taskStatusConfig } from '../helpers';
import Card from 'components/card';
import { MdClose, MdAdd } from 'react-icons/md';
import { ChevronRight, ChevronDown, CheckCircle2, Calendar, MessageSquare, Zap } from 'lucide-react';

const STORAGE_KEY = (memberId: string) => `toola_okr_1on1_${memberId}`;

function loadMeetings(memberId: string): OneOnOneMeeting[] {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY(memberId)) ?? '[]'); } catch { return []; }
}
function saveMeetings(memberId: string, meetings: OneOnOneMeeting[]) {
    localStorage.setItem(STORAGE_KEY(memberId), JSON.stringify(meetings));
}

// ── Meeting form ──────────────────────────────────────────────────────────────

const MeetingForm: React.FC<{
    memberId: string;
    memberName: string;
    memberTasks: Task[];
    onSave: (meeting: OneOnOneMeeting) => void;
    onCancel: () => void;
}> = ({ memberId, memberName, memberTasks, onSave, onCancel }) => {
    const [openPoints, setOpenPoints] = useState('');
    const [feedback, setFeedback] = useState('');
    const [blockers, setBlockers] = useState('');
    const [nextWeekGoals, setNextWeekGoals] = useState('');
    const [actionText, setActionText] = useState('');
    const [actionItems, setActionItems] = useState<OneOnOneActionItem[]>([]);

    const addAction = () => {
        if (!actionText.trim()) return;
        setActionItems((prev) => [...prev, { id: `ai-${Date.now()}`, text: actionText.trim(), done: false }]);
        setActionText('');
    };

    const handleSave = () => {
        const meeting: OneOnOneMeeting = {
            id: `1on1-${Date.now()}`,
            memberId,
            date: new Date().toISOString(),
            openPoints, feedback, blockers, nextWeekGoals,
            actionItems,
        };
        onSave(meeting);
    };

    const textareaClass = 'w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-toola-500 resize-none';
    const activeTasks = memberTasks.filter((t) => t.status !== 'done').sort((a, b) => computeSmartScore(b) - computeSmartScore(a)).slice(0, 5);

    return (
        <div className="space-y-5">
            {/* Task state of member */}
            <div className="rounded-xl bg-gray-50 p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">État des tâches de {memberName}</p>
                {activeTasks.length > 0 ? (
                    <div className="space-y-2">
                        {activeTasks.map((t) => {
                            const cfg = taskStatusConfig[t.status];
                            return (
                                <div key={t.id} className="flex items-center gap-2 text-sm">
                                    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                                    <span className="flex-1 text-navy-700 truncate">{t.title}</span>
                                    <span className="text-xs font-bold text-orange-400">{computeSmartScore(t)}</span>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-xs text-gray-400 text-center py-2">Aucune tâche active</p>
                )}
            </div>

            {/* 4 sections */}
            {[
                { key: 'openPoints', label: '📋 Points ouverts', value: openPoints, set: setOpenPoints, placeholder: 'Sujets en suspens depuis la dernière réunion…' },
                { key: 'feedback', label: '💬 Feedback', value: feedback, set: setFeedback, placeholder: 'Retours positifs et points d\'amélioration…' },
                { key: 'blockers', label: '🚧 Blocages', value: blockers, set: setBlockers, placeholder: 'Qu\'est-ce qui empêche ce membre d\'avancer ?…' },
                { key: 'nextWeekGoals', label: '🎯 Objectifs semaine prochaine', value: nextWeekGoals, set: setNextWeekGoals, placeholder: 'Les 2-3 priorités clés pour la semaine à venir…' },
            ].map(({ key, label, value, set, placeholder }) => (
                <div key={key}>
                    <label className="text-sm font-semibold text-navy-700 mb-2 block">{label}</label>
                    <textarea rows={3} value={value} onChange={(e) => set(e.target.value)} placeholder={placeholder} className={textareaClass} />
                </div>
            ))}

            {/* Action items */}
            <div>
                <label className="text-sm font-semibold text-navy-700 mb-2 block">✅ Action items → Tâches</label>
                <div className="flex gap-2 mb-3">
                    <input
                        type="text"
                        value={actionText}
                        onChange={(e) => setActionText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addAction()}
                        placeholder="Ajouter un action item…"
                        className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-toola-500"
                    />
                    <button onClick={addAction} className="linear rounded-xl bg-toola-500 px-3 py-2 text-sm font-medium text-white hover:bg-toola-600">
                        <MdAdd className="w-4 h-4" />
                    </button>
                </div>
                <div className="space-y-1.5">
                    {actionItems.map((item) => (
                        <div key={item.id} className="flex items-center gap-2 rounded-xl border border-gray-100 px-3 py-2">
                            <CheckCircle2 size={14} className="text-green-400 flex-shrink-0" />
                            <span className="text-sm text-navy-700 flex-1">{item.text}</span>
                            <button onClick={() => setActionItems((p) => p.filter((i) => i.id !== item.id))} className="text-gray-300 hover:text-red-400">
                                <MdClose className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 pt-4 border-t border-gray-100">
                <button onClick={onCancel} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                    Annuler
                </button>
                <button onClick={handleSave} className="linear flex-1 rounded-xl bg-toola-500 py-2.5 text-sm font-semibold text-white hover:bg-toola-600">
                    Sauvegarder le 1:1
                </button>
            </div>
        </div>
    );
};

// ── Past meeting ──────────────────────────────────────────────────────────────

const PastMeeting: React.FC<{ meeting: OneOnOneMeeting }> = ({ meeting }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded-xl border border-gray-100">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 text-left transition-colors">
                {open ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                <Calendar size={14} className="text-toola-400" />
                <p className="text-sm font-semibold text-navy-700 flex-1">
                    {new Date(meeting.date).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                </p>
                {meeting.actionItems.length > 0 && (
                    <span className="text-xs text-gray-400">{meeting.actionItems.filter((i) => i.done).length}/{meeting.actionItems.length} actions</span>
                )}
            </button>
            {open && (
                <div className="px-4 pb-4 border-t border-gray-50 pt-3 space-y-3">
                    {meeting.openPoints && <div><p className="text-xs font-semibold text-gray-400 mb-1">Points ouverts</p><p className="text-sm text-gray-600">{meeting.openPoints}</p></div>}
                    {meeting.blockers && <div><p className="text-xs font-semibold text-gray-400 mb-1">Blocages</p><p className="text-sm text-gray-600">{meeting.blockers}</p></div>}
                    {meeting.nextWeekGoals && <div><p className="text-xs font-semibold text-gray-400 mb-1">Objectifs semaine suivante</p><p className="text-sm text-gray-600">{meeting.nextWeekGoals}</p></div>}
                    {meeting.actionItems.length > 0 && (
                        <div>
                            <p className="text-xs font-semibold text-gray-400 mb-1.5">Action items</p>
                            <div className="space-y-1">
                                {meeting.actionItems.map((item) => (
                                    <div key={item.id} className="flex items-center gap-2 text-sm text-gray-600">
                                        <CheckCircle2 size={13} className={item.done ? 'text-green-500' : 'text-gray-300'} />
                                        <span className={item.done ? 'line-through text-gray-400' : ''}>{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

const OneOnOne: React.FC<{ state: OKRState; onAddTask?: (task: Task) => void }> = ({ state }) => {
    const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    const [meetingsMap, setMeetingsMap] = useState<Record<string, OneOnOneMeeting[]>>({});

    const getMeetings = (memberId: string) => meetingsMap[memberId] ?? loadMeetings(memberId);

    const handleSelectMember = (id: string) => {
        setSelectedMemberId(id);
        setCreating(false);
        if (!meetingsMap[id]) {
            const loaded = loadMeetings(id);
            setMeetingsMap((prev) => ({ ...prev, [id]: loaded }));
        }
    };

    const handleSave = useCallback((meeting: OneOnOneMeeting) => {
        const id = meeting.memberId;
        const next = [meeting, ...getMeetings(id)];
        saveMeetings(id, next);
        setMeetingsMap((prev) => ({ ...prev, [id]: next }));
        setCreating(false);
    }, [meetingsMap]);

    const selectedMember = state.team.find((m) => m.id === selectedMemberId);
    const memberTasks = selectedMemberId ? state.tasks.filter((t) => t.assigneeId === selectedMemberId) : [];
    const meetings = selectedMemberId ? getMeetings(selectedMemberId) : [];

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Member list */}
            <div className="space-y-3">
                <Card extra="p-4">
                    <h3 className="text-sm font-bold text-navy-700 mb-3 flex items-center gap-2">
                        <MessageSquare size={15} className="text-toola-500" /> Réunions 1:1
                    </h3>
                    <div className="space-y-2">
                        {state.team.map((m) => {
                            const count = loadMeetings(m.id).length;
                            return (
                                <button
                                    key={m.id}
                                    onClick={() => handleSelectMember(m.id)}
                                    className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-all ${selectedMemberId === m.id ? 'border-toola-200 bg-blue-50/50' : 'border-gray-100 hover:bg-gray-50'}`}
                                >
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: m.avatarColor }}>
                                        {m.avatarInitials}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-navy-700 truncate">{m.name}</p>
                                        <p className="text-xs text-gray-400">{count} réunion{count > 1 ? 's' : ''}</p>
                                    </div>
                                    <ChevronRight size={14} className="text-gray-300" />
                                </button>
                            );
                        })}
                        {state.team.length === 0 && (
                            <p className="text-sm text-gray-400 text-center py-4">Aucun membre dans l'équipe</p>
                        )}
                    </div>
                </Card>
            </div>

            {/* Right panel */}
            <div className="lg:col-span-2">
                {!selectedMember ? (
                    <Card extra="p-8 text-center">
                        <MessageSquare size={40} className="mx-auto mb-3 text-gray-200" />
                        <p className="text-gray-400 font-medium">Sélectionnez un membre</p>
                        <p className="text-sm text-gray-300 mt-1">Gérez vos réunions 1:1 ici</p>
                    </Card>
                ) : (
                    <div className="space-y-5">
                        <Card extra="p-5">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: selectedMember.avatarColor }}>
                                        {selectedMember.avatarInitials}
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-navy-700">{selectedMember.name}</h3>
                                        <p className="text-xs text-gray-400">{meetings.length} réunion{meetings.length > 1 ? 's' : ''} enregistrée{meetings.length > 1 ? 's' : ''}</p>
                                    </div>
                                </div>
                                {!creating && (
                                    <button onClick={() => setCreating(true)} className="linear flex items-center gap-2 rounded-xl bg-toola-500 px-4 py-2 text-sm font-medium text-white hover:bg-toola-600">
                                        <MdAdd className="w-4 h-4" /> Nouvelle réunion
                                    </button>
                                )}
                            </div>

                            {creating && (
                                <MeetingForm
                                    memberId={selectedMember.id}
                                    memberName={selectedMember.name}
                                    memberTasks={memberTasks}
                                    onSave={handleSave}
                                    onCancel={() => setCreating(false)}
                                />
                            )}
                        </Card>

                        {!creating && meetings.length > 0 && (
                            <div>
                                <p className="text-sm font-semibold text-gray-500 mb-3">Historique</p>
                                <div className="space-y-2">
                                    {meetings.map((m) => <PastMeeting key={m.id} meeting={m} />)}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OneOnOne;
